import { CSSProperties } from 'react';
import { IconButton, ListItem, Stack, Box, Chip, Typography, Tooltip } from '@mui/material';
import { Close as CloseIcon, DragIndicator as DragHandleIcon } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useI18nContext } from '@/i18n/i18n-react';
import type { ScheduleTemplate } from '@/types';
import { splitTags } from '@/utils/tags';

interface SortableTemplateRowProps {
    template: ScheduleTemplate;
    onRemove: () => void;
    disabled?: boolean;
    instanceId: string;
}

const SortableTemplateRow = ({
    template,
    onRemove,
    disabled,
    instanceId,
}: SortableTemplateRowProps) => {
    const { LL } = useI18nContext();

    const tags = splitTags(template.tags);
    const titleWithTags = tags.length
        ? `${tags.join(', ')} - ${template.template}`
        : template.template;

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: instanceId,
        disabled,
    });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                bgcolor: 'background.paper',
            }}
            secondaryAction={
                <Tooltip title={LL.scheduleTemplateSets.remove()}>
                    <IconButton edge="end" onClick={onRemove} size="small" disabled={disabled}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            }
        >
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center', gap: 1, pr: 4 }}>
                <Tooltip title={LL.scheduleTemplateSets.dragToReorder()}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: disabled ? 'text.disabled' : 'text.secondary',
                            cursor: disabled ? 'default' : 'grab',
                            '&:active': { cursor: disabled ? 'default' : 'grabbing' },
                        }}
                        {...attributes}
                        {...listeners}
                    >
                        <DragHandleIcon />
                    </Box>
                </Tooltip>

                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={{ xs: 0.5, md: 1.5 }}
                    sx={{ flex: 1, minWidth: 0 }}
                >
                    <Typography
                        variant="subtitle1"
                        noWrap
                        sx={{
                            fontWeight: 700,
                        }}
                    >
                        {titleWithTags}
                    </Typography>
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip
                            size="small"
                            label={`${template.duration} ${LL.scheduleModal.minutesShort()}`}
                        />
                        {template.responsible && (
                            <Chip size="small" variant="outlined" label={template.responsible} />
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </ListItem>
    );
};

export default SortableTemplateRow;
